/**
 * Incentive.js — Tab-based layout (UX friendly)
 *
 * Tab 1 — Sheet1: Case-wise Disbursement
 *   Sr No | Date | Customer | Office | File No | Product | Corp Agent | Institution
 *   Institution Payout % | Channel Payout % | Incomes | Current Disb | Connector Y/N
 *   Connector Name | Conn Payout% | Connector Payout | CF Revenue
 *
 * Tab 2 — Sheet2: Employee Incentive
 *   Per user: # | Name/Lead | Corp Disb With | Corp Disb Thru | (A) Rev to Co
 *   PDD | Invoice | Revenue | (B) BSA Payout | (C) % Considered [editable] | (D)=(A-B)×C
 *   Bottom: Not Payable → Less Cutoff → Excess → Eligible Incentive
 */
import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { api } from '@crm/shared';
import { toast } from 'react-toastify';
import {
  FiRefreshCw, FiLock, FiPlay, FiDownload,
  FiAlertCircle, FiInfo, FiActivity, FiUsers,
  FiChevronLeft, FiChevronRight,
} from 'react-icons/fi';

/* ─── Design tokens ─── */
const C = {
  navy:'#0D3B5E', navyD:'#082B47', navyM:'#1A5276', navyL:'#EBF4FF',
  gold:'#E8930A', goldL:'#FEF3DC', goldB:'#F5A623',
  green:'#15803D', greenL:'#DCFCE7', greenB:'#16A34A',
  red:'#B91C1C', redL:'#FEF2F2', redB:'#DC2626', redBord:'#FECACA',
  sky:'#0369A1', skyL:'#DBEAFE', skyB:'#0284C7',
  purple:'#6D28D9', purpleL:'#EDE9FE',
  teal:'#0D9488', tealL:'#CCFBF1', tealB:'#5EEAD4',
  orange:'#EA580C', orangeL:'#FFF7ED',
  border:'#E5E7EB', borderL:'#F3F4F6',
  bg:'#F4F6F9', white:'#FFFFFF',
  t1:'#111827', t2:'#374151', t3:'#6B7280', t4:'#9CA3AF',
};

/* ─── Helpers ─── */
const fmt    = (n) => { if (n==null||n==='') return '—'; const v=Number(n); return isNaN(v)?'—':'₹'+v.toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2}); };
const fmtPct = (n) => { const v=Number(n); return isNaN(v)?'—':v.toFixed(2)+'%'; };
const fmtN   = (n) => { const v=Number(n); return isNaN(v)?0:v; };
const fmtDate= (s) => { if(!s) return '—'; try { const d=new Date(s); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; } catch { return s; } };
const curYM  = () => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; };
const prevYM = (ym) => { const [y,m]=ym.split('-').map(Number); const d=new Date(y,m-2,1); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; };
const nextYM = (ym) => { const [y,m]=ym.split('-').map(Number); const d=new Date(y,m,1);   return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; };
const ymLabel  = (ym) => { if(!ym) return ''; try { return new Date(ym+'-01').toLocaleString('en-IN',{month:'long',year:'numeric'}); } catch { return ym; } };
const ymParams = (ym) => { const [y,m]=ym.split('-').map(Number); return {year:y,month:m}; };

/* ─── Sheet1 row calc ─── */
const calcSheet1 = (b) => {
  const disb          = fmtN(b.disbursement_amount);
  const incentiveRate = fmtN(b.incentive_rate_used ?? 0);
  const incomePct     = fmtN(b.income_pct_used     ?? 0);
  const incomes       = disb * (incentiveRate / 100) * (incomePct / 100);
  const bsaPayout     = fmtN(b.bsa_payout);
  const cfRevenue     = incomes - bsaPayout;
  return { disb, incentiveRate, incomePct, incomes, bsaPayout, cfRevenue };
};

/* ─── Sheet2 Column D ─── */
const calcD = (b, pctMap) => {
  const A   = fmtN(b.revenue_to_company);
  const B   = fmtN(b.bsa_payout);
  const pct = fmtN(pctMap?.[b.id] ?? b.percentage_considered ?? 100);
  return (A - B) * (pct / 100);
};

/* ─── Per-user bottom calc ─── */
const getBottomCalc = (record, pctMap) => {
  const bds    = record.breakdowns || [];
  const totalD = bds.reduce((s,b) => s + calcD(b,pctMap), 0);
  const cutoff = fmtN(record.user_detail?.cutoff_amount ?? record.cutoff_amount ?? 0);
  const rate   = fmtN(record.user_detail?.incentive_percentage ?? record.incentive_percentage ?? 0);
  const excess = Math.max(0, totalD - cutoff);
  return { totalD, cutoff, rate, excess, eligInc: excess * rate / 100 };
};

/* ─── Shimmer / Spinner ─── */
const Sh   = ({h=20,w='100%',r=6}) => <div style={{height:h,width:w,borderRadius:r,marginBottom:8,background:'linear-gradient(90deg,#F1F5F9 25%,#E2E8F0 50%,#F1F5F9 75%)',backgroundSize:'400px 100%',animation:'incShimmer 1.4s infinite'}}/>;
const Spin = ({size=13}) => <div style={{width:size,height:size,borderRadius:'50%',border:'2px solid rgba(255,255,255,0.35)',borderTopColor:'#fff',animation:'incSpin 0.8s linear infinite',flexShrink:0}}/>;
const YN   = (v) => (v==='Y'||v===true)
  ? <span style={{color:C.green,fontWeight:700,fontSize:12}}>Y</span>
  : <span style={{color:C.redB, fontWeight:600,fontSize:12}}>N</span>;

const AVATAR_COLORS = ['#3B82F6','#8B5CF6','#EC4899','#14B8A6','#F59E0B'];
const aColor = (name) => AVATAR_COLORS[(name?.charCodeAt(0)||0) % AVATAR_COLORS.length];

/* ─── CSV Export ─── */
const exportCsv = (data, pctMap, ym, label, format = 'csv') => {
  const q   = (v) => { const s=String(v??''); return (s.includes(',')||s.includes('"')||s.includes('\n'))?'"'+s.replace(/"/g,'""')+'"':s; };
  const row = (arr) => arr.map(q).join(',');

  const lines = [
    `Incentive Report - ${label} (Case Wise Disbursement)`,
    row(['Sr No.','Date of Disbursement','Customer Name','Office','File No','Product',
      'Corporate Agent','Name of Institution','Institution Payout %','Channel Payout %','Incomes',
      'Sanction Amount','STATUS','Current Disbursed Amount','Internal Person / Sales Exe',
      'Connector Yes/No','Connector Name','Connector Payout %age','CONNECTOR PAYOUT','CF Revenue']),
  ];
  let sr1=1;
  data.forEach(record => {
    const u    = record.user_detail || {};
    const exec = [u.first_name,u.last_name].filter(Boolean).join(' ') || u.email || '';
    (record.breakdowns||[]).forEach(b => {
      const {disb,incentiveRate,incomePct,incomes,bsaPayout,cfRevenue} = calcSheet1(b);
      const hasBsa = bsaPayout > 0;
      lines.push(row([
        sr1++, b.disbursement_date||'', b.customer_name||'', b.location||'',
        b.lead_no||'', b.product||'', b.corp_disb_thru||'', b.corp_disb_with||'',
        incentiveRate, incomePct, incomes.toFixed(2),
        b.sanctioned_amount||'', b.disbursement_status||'', disb.toFixed(2), exec,
        hasBsa?'Yes':'No', hasBsa?(b.corp_disb_thru||''):'',
        hasBsa?fmtN(b.bsa_payout_pct??0):0,
        bsaPayout.toFixed(2), cfRevenue.toFixed(2),
      ]));
    });
  });

  lines.push('','',`Incentive Summary - ${label}`);
  let sr2=1;
  data.forEach(record => {
    const u    = record.user_detail || {};
    const name = [u.first_name,u.last_name].filter(Boolean).join(' ') || u.email || '';
    const bc   = getBottomCalc(record, pctMap);
    lines.push('',row(['',name,'','','','','','','','','']));
    lines.push(row(['#','Name / Lead','Corp Disb With','Corp Disb Thru',
      '(A) Revenue to Co','PDD','Invoice','Revenue',
      '(B) BSA Payout','(C) % Considered','(D) = (A-B)×C']));
    (record.breakdowns||[]).forEach(b => {
      const D   = calcD(b, pctMap);
      const pct = fmtN(pctMap?.[b.id] ?? b.percentage_considered ?? 100);
      lines.push(row([
        sr2++, `${b.lead_no||'#'+b.lead} — ${b.customer_name||''}`,
        b.corp_disb_with||'', b.corp_disb_thru||'',
        fmtN(b.revenue_to_company).toFixed(2),
        b.pdd_approved||'N', b.invoice_raised||'N', b.revenue_realised||'N',
        fmtN(b.bsa_payout).toFixed(2), pct, D.toFixed(2),
      ]));
    });
    lines.push(
      row(['','','','','','','','','','Not Payable (Total D):',bc.totalD.toFixed(2)]),
      row(['','','','','','','','','','Less Cut-off:',bc.cutoff.toFixed(2)]),
      row(['','','','','','','','','','Excess (E):',bc.excess.toFixed(2)]),
      row(['','','','','','','','','',`Rate (${bc.rate}%):`,'']),
      row(['','','','','','','','','','Eligible Incentive:',bc.eligInc.toFixed(2)]),
    );
  });

  const csv = '\uFEFF' + lines.join('\n');
  const a   = document.createElement('a');
  a.href    = URL.createObjectURL(new Blob([csv],{type:format === 'excel' ? 'application/vnd.ms-excel;charset=utf-8;' : 'text/csv;charset=utf-8;'}));
  a.download= `incentive_${ym}.${format === 'excel' ? 'xls' : 'csv'}`;
  a.click(); URL.revokeObjectURL(a.href);
};

const exportExcelReport = (data, pctMap, ym, label) => {
  const esc = (v) => String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  const money = (v) => Number(v || 0).toFixed(2);
  const pct = (v) => Number(v || 0).toFixed(4);

  const allRows = [];
  data.forEach(record => {
    const u = record.user_detail || {};
    const exec = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || '';
    (record.breakdowns || []).forEach(b => allRows.push({ ...b, _exec: exec }));
  });

  const totals = allRows.reduce((acc, b) => {
    const c = calcSheet1(b);
    acc.disb += c.disb;
    acc.incomes += c.incomes;
    acc.bsa += c.bsaPayout;
    acc.cfRevenue += c.cfRevenue;
    return acc;
  }, { disb: 0, incomes: 0, bsa: 0, cfRevenue: 0 });

  const grand = data.reduce((acc, record) => {
    const bc = getBottomCalc(record, pctMap);
    acc.totalD += bc.totalD;
    acc.incentive += bc.eligInc;
    acc.cutoff += bc.cutoff;
    acc.excess += bc.excess;
    return acc;
  }, { totalD: 0, incentive: 0, cutoff: 0, excess: 0 });

  const caseRows = allRows.map((b, i) => {
    const c = calcSheet1(b);
    const hasBsa = c.bsaPayout > 0;
    return `
      <tr>
        <td class="center">${i + 1}</td>
        <td>${esc(fmtDate(b.disbursement_date))}</td>
        <td class="strong">${esc(b.customer_name || '')}</td>
        <td>${esc(b.location || '')}</td>
        <td class="mono">${esc(b.lead_no || `#${b.lead}`)}</td>
        <td>${esc(b.product || '')}</td>
        <td>${esc(b.corp_disb_thru || '')}</td>
        <td>${esc(b.corp_disb_with || '')}</td>
        <td class="num">${pct(c.incentiveRate)}</td>
        <td class="num">${pct(c.incomePct)}</td>
        <td class="money blue">${money(c.incomes)}</td>
        <td class="money">${money(c.disb)}</td>
        <td class="center">${hasBsa ? 'Yes' : 'No'}</td>
        <td>${esc(hasBsa ? (b.corp_disb_thru || '') : '')}</td>
        <td class="num">${hasBsa ? pct(fmtN(b.bsa_payout_pct ?? 0)) : ''}</td>
        <td class="money red">${money(c.bsaPayout)}</td>
        <td class="money green">${money(c.cfRevenue)}</td>
        <td>${esc(b._exec || '')}</td>
      </tr>`;
  }).join('');

  const userSections = data.map(record => {
    const u = record.user_detail || {};
    const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || `User #${record.user}`;
    const bc = getBottomCalc(record, pctMap);
    const rows = (record.breakdowns || []).map((b, i) => {
      const considered = fmtN(pctMap?.[b.id] ?? b.percentage_considered ?? 100);
      const d = calcD(b, pctMap);
      return `
        <tr>
          <td class="center">${i + 1}</td>
          <td><span class="strong">${esc(b.customer_name || '')}</span><br/><span class="muted">${esc(b.lead_no || `#${b.lead}`)}</span></td>
          <td>${esc(b.corp_disb_with || '')}</td>
          <td>${esc(b.corp_disb_thru || '')}</td>
          <td class="money blue">${money(b.revenue_to_company)}</td>
          <td class="center">${esc(b.pdd_approved || 'N')}</td>
          <td class="center">${esc(b.invoice_raised || 'N')}</td>
          <td class="center">${esc(b.revenue_realised || 'N')}</td>
          <td class="money red">${money(b.bsa_payout)}</td>
          <td class="num">${Number(considered).toFixed(2)}%</td>
          <td class="money green">${money(d)}</td>
        </tr>`;
    }).join('');

    return `
      <h2>${esc(name)}</h2>
      <table class="report">
        <thead>
          <tr>
            <th>#</th><th>Name / Lead</th><th>Corp Disb With</th><th>Corp Disb Thru</th>
            <th>(A) Revenue to Co</th><th>PDD</th><th>Invoice</th><th>Revenue</th>
            <th>(B) BSA Payout</th><th>(C) % Considered</th><th>(D) = (A-B) x C</th>
          </tr>
        </thead>
        <tbody>${rows || '<tr><td colspan="11" class="center muted">No cases</td></tr>'}</tbody>
      </table>
      <table class="summary small">
        <tr><td>Not Payable (Total D)</td><td class="money">${money(bc.totalD)}</td></tr>
        <tr><td>Less: Cutoff</td><td class="money red">${money(bc.cutoff)}</td></tr>
        <tr><td>Excess</td><td class="money green">${money(bc.excess)}</td></tr>
        <tr><td>Rate</td><td class="num">${Number(bc.rate || 0).toFixed(2)}%</td></tr>
        <tr class="grand"><td>Eligible Incentive</td><td class="money">${money(bc.eligInc)}</td></tr>
      </table>`;
  }).join('');

  const html = `
    <html>
      <head>
        <meta charset="UTF-8" />
        <style>
          body { font-family: Arial, sans-serif; color: #111827; }
          h1 { background: #0D3B5E; color: #ffffff; padding: 14px 16px; margin: 0; font-size: 20px; }
          h2 { color: #0D3B5E; font-size: 15px; margin: 22px 0 8px; }
          .subtitle { background: #EBF4FF; color: #0D3B5E; padding: 8px 16px; margin-bottom: 14px; }
          table { border-collapse: collapse; margin-bottom: 16px; }
          .kpis td { border: 1px solid #D1D5DB; padding: 8px 12px; font-weight: 700; }
          .kpis .label { background: #F3F4F6; color: #374151; }
          .report th { background: #0D3B5E; color: #ffffff; border: 1px solid #082B47; padding: 8px; font-size: 12px; }
          .report td { border: 1px solid #D1D5DB; padding: 7px; font-size: 12px; vertical-align: top; }
          .report tbody tr:nth-child(even) td { background: #F8FAFC; }
          .summary td { border: 1px solid #D1D5DB; padding: 7px 12px; font-weight: 700; }
          .summary .grand td { background: #FEF3DC; color: #92400E; font-size: 13px; }
          .small { width: 420px; }
          .center { text-align: center; }
          .num, .money { text-align: right; mso-number-format: "0.00"; }
          .strong { font-weight: 700; }
          .mono { font-family: Consolas, monospace; }
          .muted { color: #6B7280; font-size: 11px; }
          .blue { background: #EFF6FF; color: #0D3B5E; font-weight: 700; }
          .red { background: #FEF2F2; color: #B91C1C; font-weight: 700; }
          .green { background: #DCFCE7; color: #15803D; font-weight: 700; }
          .total td { background: #F0F9FF; font-weight: 800; }
        </style>
      </head>
      <body>
        <h1>Credi Flex Incentive Report</h1>
        <div class="subtitle">Period: ${esc(label)} | Generated: ${esc(new Date().toLocaleString('en-IN'))}</div>
        <table class="kpis">
          <tr>
            <td class="label">Cases</td><td>${allRows.length}</td>
            <td class="label">Total Disbursed</td><td class="money">${money(totals.disb)}</td>
            <td class="label">Total Incomes</td><td class="money">${money(totals.incomes)}</td>
            <td class="label">BSA Payout</td><td class="money">${money(totals.bsa)}</td>
            <td class="label">Total Incentive</td><td class="money">${money(grand.incentive)}</td>
          </tr>
        </table>

        <h2>Sheet 1 - Case-wise Disbursement</h2>
        <table class="report">
          <thead>
            <tr>
              <th>#</th><th>Date</th><th>Customer</th><th>Office</th><th>File No</th><th>Product</th>
              <th>Corporate Agent</th><th>Institution</th><th>Institution Payout %</th><th>Channel Payout %</th>
              <th>Incomes</th><th>Current Disbursed</th><th>Connector</th><th>Connector Name</th>
              <th>Connector Payout %</th><th>Connector Payout</th><th>CF Revenue</th><th>Executive</th>
            </tr>
          </thead>
          <tbody>${caseRows || '<tr><td colspan="18" class="center muted">No cases</td></tr>'}</tbody>
          <tfoot>
            <tr class="total">
              <td colspan="10" class="num">TOTAL</td>
              <td class="money">${money(totals.incomes)}</td>
              <td class="money">${money(totals.disb)}</td>
              <td colspan="3"></td>
              <td class="money">${money(totals.bsa)}</td>
              <td class="money">${money(totals.cfRevenue)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>

        <h2>Sheet 2 - Employee Incentive</h2>
        ${userSections}
      </body>
    </html>`;

  const blob = new Blob(['\uFEFF' + html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `incentive_${ym}.xls`;
  a.click();
  URL.revokeObjectURL(a.href);
};

const getPctPayload = (data, pctMap) => {
  const payload = {};
  data.forEach(record => (record.breakdowns || []).forEach(b => {
    payload[b.id] = fmtN(pctMap?.[b.id] ?? b.percentage_considered ?? 100);
  }));
  return payload;
};

/* ══════════════════════════════════════════════════════════════
   SHEET1 COMPONENT — Case-wise Disbursement
══════════════════════════════════════════════════════════════ */
function Sheet1Tab({ calcData, loading, calculating }) {
  if (loading||calculating) return (
    <div style={{background:C.white,borderRadius:12,border:`1px solid ${C.border}`,padding:18}}>
      {[0,1,2,3,4].map(i=><Sh key={i} h={48}/>)}
    </div>
  );
  if (!calcData.length) return (
    <div style={{background:C.white,borderRadius:12,border:`1px solid ${C.border}`,padding:'48px 24px',textAlign:'center'}}>
      <div style={{fontSize:13,color:C.t4}}>Calculate to see case-wise data</div>
    </div>
  );

  // Flatten all breakdowns across all users
  const allRows = [];
  calcData.forEach(record => {
    const u    = record.user_detail || {};
    const exec = [u.first_name,u.last_name].filter(Boolean).join(' ') || u.email || '—';
    (record.breakdowns||[]).forEach(b => {
      allRows.push({ ...b, _exec: exec });
    });
  });

  const totalIncomes   = allRows.reduce((s,b) => s + calcSheet1(b).incomes,   0);
  const totalDisb      = allRows.reduce((s,b) => s + calcSheet1(b).disb,      0);
  const totalBsaPayout = allRows.reduce((s,b) => s + calcSheet1(b).bsaPayout, 0);
  const totalCfRevenue = allRows.reduce((s,b) => s + calcSheet1(b).cfRevenue, 0);

  const thSt = (align='left') => ({
    padding:'9px 11px', fontSize:10, fontWeight:800, color:C.t2,
    textTransform:'uppercase', letterSpacing:'0.05em',
    textAlign:align, borderBottom:`1px solid ${C.border}`,
    whiteSpace:'nowrap', background:'#F7F9FB',
  });

  return (
    <div style={{background:C.white,borderRadius:12,border:`1px solid ${C.border}`,overflow:'hidden',boxShadow:'0 2px 6px rgba(13,59,94,0.06)'}}>
      {/* Header */}
      <div style={{padding:'12px 18px',borderBottom:`1px solid ${C.borderL}`,background:'#FAFBFC',
        display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <FiActivity size={14} style={{color:C.tealB}}/>
          <span style={{fontSize:13.5,fontWeight:700,color:C.t1}}>Case-wise Disbursement</span>
          <span style={{background:C.tealL,color:C.tealB,padding:'2px 8px',borderRadius:20,fontSize:10.5,fontWeight:700}}>
            {allRows.length} cases
          </span>
        </div>
        {/* KPI pills */}
        <div style={{display:'flex',gap:8,flexWrap:'nowrap',overflowX:'auto',maxWidth:'100%',paddingBottom:2}}>
          {[
            {label:'Total Disbursed', val:fmt(totalDisb),      bg:C.greenL,   color:C.greenB},
            {label:'Total Incomes',   val:fmt(totalIncomes),   bg:C.navyL,    color:C.navy},
            {label:'BSA Payout',      val:fmt(totalBsaPayout), bg:C.goldL,    color:C.gold},
            {label:'CF Revenue',      val:fmt(totalCfRevenue), bg:C.tealL,    color:C.tealB},
          ].map(p=>(
            <div key={p.label} style={{padding:'4px 12px',borderRadius:20,background:p.bg,
              fontSize:11.5,fontWeight:600,color:p.color,display:'flex',gap:5,alignItems:'center',
              whiteSpace:'nowrap',flex:'0 0 auto'}}>
              <span style={{color:C.t4,fontWeight:400,whiteSpace:'nowrap'}}>{p.label}:</span>
              <span style={{whiteSpace:'nowrap'}}>{p.val}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{overflowX:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',minWidth:1300}}>
          <thead>
            <tr>
              {[
                ['#',              '40px', 'center'],
                ['Date',           '90px', 'center'],
                ['Customer Name',  '150px','left'  ],
                ['Office',         '85px', 'left'  ],
                ['File No',        '110px','left'  ],
                ['Product',        '75px', 'left'  ],
                ['Corp. Agent',    '120px','left'  ],
                ['Institution',    '120px','left'  ],
                ['Inst. Payout %', '95px', 'right' ],
                ['Channel %',      '80px', 'right' ],
                ['Incomes',        '115px','right' ],
                ['Cur. Disb Amt',  '115px','right' ],
                ['Connector',      '75px', 'center'],
                ['Conn. Name',     '110px','left'  ],
                ['Conn. Payout%',  '90px', 'right' ],
                ['Conn. Payout',   '110px','right' ],
                ['CF Revenue',     '115px','right' ],
                ['Executive',      '120px','left'  ],
              ].map(([h,w,a])=>(
                <th key={h} style={{...thSt(a),width:w,minWidth:w}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allRows.map((b,bi)=>{
              const isLast = bi===allRows.length-1;
              const {disb,incentiveRate,incomePct,incomes,bsaPayout,cfRevenue} = calcSheet1(b);
              const hasBsa = bsaPayout>0;
              const bg     = bi%2===0?C.white:'#FAFCFF';
              const td     = {padding:'9px 11px',borderBottom:isLast?'none':`1px solid ${C.borderL}`,fontSize:12.5};
              return (
                <tr key={b.id||bi} className="inc-tr" style={{background:bg}}>
                  <td style={{...td,textAlign:'center',color:C.t4,fontSize:11}}>{bi+1}</td>
                  <td style={{...td,textAlign:'center',color:C.t3,whiteSpace:'nowrap'}}>{fmtDate(b.disbursement_date)}</td>
                  <td style={{...td,fontWeight:700,color:C.t1}}>{b.customer_name||'—'}</td>
                  <td style={{...td,color:C.t3}}>{b.location||'—'}</td>
                  <td style={{...td,color:C.navy,fontFamily:'monospace',fontSize:11.5}}>{b.lead_no||`#${b.lead}`}</td>
                  <td style={{...td,color:C.t2}}>{b.product||'—'}</td>
                  <td style={{...td,color:C.teal,fontWeight:600}}>{b.corp_disb_thru||'—'}</td>
                  <td style={{...td,color:C.sky, fontWeight:600}}>{b.corp_disb_with||'—'}</td>
                  <td style={{...td,textAlign:'right',color:C.purple,fontFamily:'monospace'}}>
                    {incentiveRate>0?incentiveRate.toFixed(2)+'%':'—'}
                  </td>
                  <td style={{...td,textAlign:'right',color:C.orange,fontFamily:'monospace'}}>
                    {incomePct>0?incomePct.toFixed(2)+'%':'—'}
                  </td>
                  <td style={{...td,textAlign:'right',fontWeight:700,color:C.navy,background:'#EFF6FF'}}>
                    {incomes>0?fmt(incomes):'—'}
                  </td>
                  <td style={{...td,textAlign:'right',color:C.greenB,fontWeight:600}}>{fmt(disb)}</td>
                  <td style={{...td,textAlign:'center'}}>
                    {hasBsa
                      ?<span style={{background:C.greenL,color:C.green,padding:'2px 8px',borderRadius:20,fontSize:11,fontWeight:700}}>Yes</span>
                      :<span style={{background:C.borderL,color:C.t4,  padding:'2px 8px',borderRadius:20,fontSize:11}}>No</span>}
                  </td>
                  <td style={{...td,color:C.t2}}>{hasBsa?(b.corp_disb_thru||'—'):'—'}</td>
                  <td style={{...td,textAlign:'right',color:C.t3,fontFamily:'monospace'}}>
                    {hasBsa&&b.bsa_payout_pct?fmtN(b.bsa_payout_pct).toFixed(2)+'%':'—'}
                  </td>
                  <td style={{...td,textAlign:'right',fontWeight:700,color:C.redB,background:'#FEF2F2'}}>
                    {hasBsa?fmt(bsaPayout):'—'}
                  </td>
                  <td style={{...td,textAlign:'right',fontWeight:800,color:C.green,background:C.greenL}}>
                    {incomes>0?fmt(cfRevenue):'—'}
                  </td>
                  <td style={{...td,color:C.t3,fontSize:12}}>{b._exec||'—'}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{background:'#F0F9FF',borderTop:`2px solid ${C.border}`}}>
              <td colSpan={10} style={{padding:'9px 11px',fontSize:12,fontWeight:700,color:C.t2,textAlign:'right'}}>
                TOTAL ({allRows.length} cases):
              </td>
              <td style={{padding:'9px 11px',textAlign:'right',fontWeight:800,color:C.navy,fontSize:13,background:'#EFF6FF'}}>{fmt(totalIncomes)}</td>
              <td style={{padding:'9px 11px',textAlign:'right',fontWeight:800,color:C.greenB,fontSize:13}}>{fmt(totalDisb)}</td>
              <td colSpan={3}/>
              <td style={{padding:'9px 11px',textAlign:'right',fontWeight:800,color:C.redB,fontSize:13,background:'#FEF2F2'}}>{fmt(totalBsaPayout)}</td>
              <td style={{padding:'9px 11px',textAlign:'right',fontWeight:800,color:C.green,fontSize:13,background:C.greenL}}>{fmt(totalCfRevenue)}</td>
              <td/>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   SHEET2 COMPONENT — Employee Incentive
══════════════════════════════════════════════════════════════ */
function Sheet2Tab({ calcData, loading, calculating, isLocked, pctMap, onPctChange }) {
  if (loading||calculating) return (
    <div style={{background:C.white,borderRadius:12,border:`1px solid ${C.border}`,padding:18}}>
      {[0,1,2,3].map(i=><Sh key={i} h={52}/>)}
    </div>
  );
  if (!calcData.length) return (
    <div style={{background:C.white,borderRadius:12,border:`1px solid ${C.border}`,padding:'48px 24px',textAlign:'center'}}>
      <div style={{fontSize:13,color:C.t4}}>Calculate to see employee incentive data</div>
    </div>
  );

  const thSt = (align='left') => ({
    padding:'9px 11px', fontSize:10, fontWeight:800, color:C.t2,
    textTransform:'uppercase', letterSpacing:'0.05em',
    textAlign:align, borderBottom:`1px solid ${C.border}`,
    whiteSpace:'nowrap', background:'#F7F9FB',
  });

  return (
    <div style={{display:'flex',flexDirection:'column',gap:16}}>
      {calcData.map((record, ri) => {
        const u    = record.user_detail || {};
        const name = [u.first_name,u.last_name].filter(Boolean).join(' ') || u.email || `User #${record.user}`;
        const ac   = aColor(name);
        const bds  = record.breakdowns || [];
        const bc   = getBottomCalc(record, pctMap);

        return (
          <div key={record.id||ri} style={{background:C.white,borderRadius:12,
            border:`1px solid ${C.border}`,overflow:'hidden',
            boxShadow:'0 2px 8px rgba(13,59,94,0.06)'}}>

            {/* User header */}
            <div style={{padding:'14px 18px',
              background:'linear-gradient(90deg,#EBF4FF,#F8FAFC)',
              borderBottom:`1px solid ${C.border}`,
              display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
              <div style={{width:38,height:38,borderRadius:'50%',
                background:ac+'20',border:`1.5px solid ${ac}40`,color:ac,
                display:'flex',alignItems:'center',justifyContent:'center',
                fontWeight:700,fontSize:13,flexShrink:0}}>
                {name.split(' ').map(p=>p[0]).slice(0,2).join('').toUpperCase()||'?'}
              </div>
              <div>
                <div style={{fontSize:14,fontWeight:800,color:C.t1}}>{name}</div>
                <div style={{fontSize:11.5,color:C.t4,marginTop:1}}>
                  {u.email||''}{u.designation?` · ${u.designation}`:''} · {bds.length} case{bds.length!==1?'s':''}
                </div>
              </div>
              <div style={{flex:1}}/>
              {/* Summary pills */}
              {[
                {label:'Σ D',        val:fmt(bc.totalD),  bg:C.purpleL,color:C.purple},
                {label:'Cutoff',     val:fmt(bc.cutoff),  bg:C.redL,   color:C.redB  },
                {label:'Excess',     val:fmt(bc.excess),  bg:C.greenL, color:C.greenB},
                {label:'Incentive',  val:fmt(bc.eligInc), bg:C.goldL,  color:C.gold  },
              ].map(p=>(
                <div key={p.label} style={{padding:'4px 12px',borderRadius:20,background:p.bg,
                  fontSize:11.5,fontWeight:700,color:p.color,display:'flex',gap:5,alignItems:'center'}}>
                  <span style={{fontWeight:400,color:C.t4}}>{p.label}:</span>{p.val}
                </div>
              ))}
            </div>

            {bds.length===0 ? (
              <div style={{padding:'24px',textAlign:'center',fontSize:13,color:C.t4}}>
                No case breakdown — run Calculate after disbursement data is available
              </div>
            ) : (
              <>
                {/* Breakdown table */}
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse',minWidth:900}}>
                    <thead>
                      <tr>
                        {[
                          ['#',              '36px', 'center'],
                          ['Name / Lead',    '160px','left'  ],
                          ['Corp Disb With', '130px','left'  ],
                          ['Corp Disb Thru', '130px','left'  ],
                          ['(A) Rev to Co',  '120px','right' ],
                          ['PDD',            '55px', 'center'],
                          ['Invoice',        '55px', 'center'],
                          ['Revenue',        '55px', 'center'],
                          ['(B) BSA Payout', '115px','right' ],
                          ['(C) % Consid.',  '110px','center'],
                          ['(D) = (A−B)×C',  '110px','right' ],
                        ].map(([h,w,a])=>(
                          <th key={h} style={{...thSt(a),width:w,minWidth:w}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {bds.map((b,bi)=>{
                        const isLast = bi===bds.length-1;
                        const D   = calcD(b,pctMap);
                        const pct = fmtN(pctMap?.[b.id] ?? b.percentage_considered ?? 100);
                        const bg  = bi%2===0?C.white:'#FAFCFF';
                        const td  = {padding:'9px 11px',borderBottom:isLast?'none':`1px solid ${C.borderL}`};
                        return (
                          <tr key={b.id||bi} className="inc-tr" style={{background:bg}}>
                            <td style={{...td,textAlign:'center',fontSize:11,color:C.t4}}>{bi+1}</td>
                            <td style={{...td}}>
                              <div style={{fontWeight:700,fontSize:12.5,color:C.t1}}>{b.customer_name||'—'}</div>
                              <div style={{fontSize:10.5,color:C.t4,fontFamily:'monospace',marginTop:1}}>{b.lead_no||`#${b.lead}`}</div>
                            </td>
                            <td style={{...td,fontSize:12,color:C.sky, fontWeight:600}}>{b.corp_disb_with||'—'}</td>
                            <td style={{...td,fontSize:12,color:C.teal,fontWeight:600}}>{b.corp_disb_thru||'—'}</td>
                            {/* A */}
                            <td style={{...td,textAlign:'right',fontSize:13,fontWeight:700,color:C.navy}}>{fmt(b.revenue_to_company)}</td>
                            <td style={{...td,textAlign:'center'}}>{YN(b.pdd_approved)}</td>
                            <td style={{...td,textAlign:'center'}}>{YN(b.invoice_raised)}</td>
                            <td style={{...td,textAlign:'center'}}>{YN(b.revenue_realised)}</td>
                            {/* B */}
                            <td style={{...td,textAlign:'right',fontSize:12.5,color:C.t2}}>{fmt(b.bsa_payout)}</td>
                            {/* C — editable */}
                            <td style={{...td,textAlign:'center'}}>
                              {isLocked ? (
                                <span style={{fontSize:12.5,fontWeight:600,color:C.t2}}>{pct.toFixed(0)}%</span>
                              ) : (
                                <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:3}}>
                                  <input
                                    type="number" min={0} max={100} step={1}
                                    value={pct}
                                    onChange={e => onPctChange(b.id, e.target.value)}
                                    className="inc-pct"
                                    style={{width:52,height:30,border:`1.5px solid ${C.border}`,
                                      borderRadius:7,padding:'0 6px',fontSize:12.5,fontWeight:600,
                                      color:C.t1,textAlign:'center',fontFamily:'inherit',outline:'none',
                                      background:pct<100?'#FEF3DC':'#F8FAFC'}}
                                    onFocus={e=>e.target.style.borderColor=C.gold}
                                    onBlur={e =>e.target.style.borderColor=C.border}
                                  />
                                  <span style={{fontSize:12,color:C.t4}}>%</span>
                                </div>
                              )}
                            </td>
                            {/* D */}
                            <td style={{...td,textAlign:'right'}}>
                              <span style={{fontSize:13,fontWeight:800,color:C.tealB}}>{fmt(D.toFixed(2))}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{background:'#F0F9FF',borderTop:`1px solid ${C.border}`}}>
                        <td colSpan={10} style={{padding:'9px 11px',textAlign:'right',fontSize:12,fontWeight:700,color:C.t2}}>
                          Total Column D (Not Payable):
                        </td>
                        <td style={{padding:'9px 11px',textAlign:'right'}}>
                          <span style={{fontSize:13,fontWeight:900,color:C.navy}}>{fmt(bc.totalD.toFixed(2))}</span>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Bottom calculation */}
                <div style={{padding:'16px 20px',
                  background:'linear-gradient(135deg,#F8FAFC,#EBF4FF)',
                  borderTop:`1px solid ${C.borderL}`,
                  display:'flex',alignItems:'flex-start',gap:24,flexWrap:'wrap'}}>
                  <div style={{flex:1,minWidth:280}}>
                    <div style={{fontSize:11,fontWeight:800,color:C.t4,
                      textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:12}}>
                      Incentive Calculation
                    </div>
                    {[
                      {label:'Not Payable (Total D)', val:bc.totalD,  color:C.navy,   bold:false},
                      {label:'Less: Cut-off',          val:-bc.cutoff, color:C.redB,   bold:false, sub:`₹${Number(bc.cutoff).toLocaleString('en-IN')} from User Master`},
                      {label:'Excess (E)',             val:bc.excess,  color:C.greenB, bold:true },
                      {label:`Rate: ${fmtPct(bc.rate)}`,val:null,      color:C.t3,     bold:false, sub:'from User Master'},
                    ].map((r,i)=>(
                      <div key={i} style={{display:'flex',justifyContent:'space-between',
                        alignItems:'baseline',padding:'5px 10px',
                        background:i===2?C.greenL:'transparent',borderRadius:7,
                        borderTop:i===2?`1px solid ${C.greenB}30`:'none'}}>
                        <span style={{fontSize:12.5,color:C.t2,fontWeight:r.bold?700:400}}>
                          {r.label}
                          {r.sub&&<span style={{fontSize:10.5,color:C.t4,marginLeft:6}}>({r.sub})</span>}
                        </span>
                        {r.val!==null&&(
                          <span style={{fontSize:13,fontWeight:r.bold?800:600,color:r.color}}>
                            {r.val<0?`−${fmt(-r.val)}`:fmt(r.val)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                  {/* Eligible Incentive box */}
                  <div style={{background:C.white,borderRadius:12,padding:'16px 22px',
                    border:`2px solid ${C.goldB}40`,textAlign:'center',
                    boxShadow:'0 2px 10px rgba(245,166,35,0.15)',minWidth:180}}>
                    <div style={{fontSize:10.5,fontWeight:700,color:C.t4,
                      textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:6}}>
                      Eligible Incentive
                    </div>
                    <div style={{fontSize:26,fontWeight:900,
                      color:bc.eligInc>0?C.gold:C.redB,
                      letterSpacing:'-0.03em',lineHeight:1}}>
                      {fmt(bc.eligInc.toFixed(2))}
                    </div>
                    <div style={{fontSize:11,color:C.t4,marginTop:6}}>
                      {fmt(bc.excess)} × {fmtPct(bc.rate)}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        );
      })}

      {/* Grand total — multiple users */}
      {calcData.length > 1 && (
        <div style={{background:'linear-gradient(135deg,#0D3B5E,#1A5276)',borderRadius:12,
          padding:'16px 22px',display:'flex',gap:20,flexWrap:'wrap',alignItems:'center'}}>
          <span style={{fontSize:13,fontWeight:700,color:'rgba(255,255,255,0.65)'}}>
            Grand Total ({calcData.length} users)
          </span>
          {calcData.reduce((acc,r)=>{
            const bc=getBottomCalc(r,pctMap);
            acc.totalD  +=bc.totalD;
            acc.eligInc +=bc.eligInc;
            return acc;
          },{totalD:0,eligInc:0}) && null /* just compute */}
          {(()=>{
            const g = calcData.reduce((a,r)=>{
              const bc=getBottomCalc(r,pctMap);
              a.totalD+=bc.totalD; a.eligInc+=bc.eligInc;
              a.disb+=fmtN(r.total_disbursement);
              a.bsa +=fmtN(r.total_bsa_payout);
              return a;
            },{totalD:0,eligInc:0,disb:0,bsa:0});
            return [
              {label:'Total Disbursed', val:g.disb,    color:'#86EFAC'},
              {label:'BSA Payout',      val:g.bsa,     color:'#FCA5A5'},
              {label:'Σ D',             val:g.totalD,  color:'#5EEAD4'},
              {label:'Total Incentive', val:g.eligInc, color:'#FCD34D'},
            ].map(t=>(
              <div key={t.label} style={{textAlign:'center'}}>
                <div style={{fontSize:10,color:'rgba(255,255,255,0.45)',
                  textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:3}}>{t.label}</div>
                <div style={{fontSize:15,fontWeight:800,color:t.color}}>{fmt(t.val)}</div>
              </div>
            ));
          })()}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   MAIN
══════════════════════════════════════════════════════════════ */
export default function Incentive() {
  const [ym,          setYm]          = useState(prevYM(curYM()));
  const [activeTab,   setActiveTab]   = useState('sheet1');
  const [users,       setUsers]       = useState([]);
  const [selUser,     setSelUser]     = useState('');
  const [calcData,    setCalcData]    = useState([]);
  const [calculating, setCalculating] = useState(false);
  const [locking,     setLocking]     = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [calcDone,    setCalcDone]    = useState(false);
  const [isLocked,    setIsLocked]    = useState(false);
  const [exporting,   setExporting]   = useState(false);
  const [pctMap,      setPctMap]      = useState({});

  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); }
    catch { return {}; }
  })();
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const all = [];
        let page = 1;
        while (true) {
          const r = await api.getUsers({ page, page_size:100, size:100 });
          const arr = Array.isArray(r?.data?.results) ? r.data.results : Array.isArray(r?.data) ? r.data : [];
          all.push(...arr);
          if (!r?.data?.next || !arr.length) break;
          page += 1;
        }
        const unique = Array.from(new Map(all.map(u => [String(u.id), u])).values());
        const list = unique.length ? unique : (currentUser?.id ? [currentUser] : []);
        setUsers(list);
      } catch {
        if (currentUser?.id) {
          setUsers([currentUser]);
        }
      }
    };
    loadUsers();
  }, []); // eslint-disable-line

  useEffect(() => {
    setCalcDone(false); setCalcData([]); setIsLocked(false); setPctMap({});
    loadExisting();
  }, [ym, selUser]); // eslint-disable-line

  const loadExisting = async () => {
    setLoading(true);
    try {
      const params = ymParams(ym);
      if (selUser) params.user_id = selUser;
      const res  = await api.getIncentives(params);
      const d    = res?.data;
      const rows = applySelectedUserFilter(extractIncentiveRows(d));
      if (rows.length>0) {
        setCalcData(rows); setCalcDone(true);
        setIsLocked(rows.some(r=>r.is_locked));
        initPctMap(rows);
      }
    } catch {}
    finally { setLoading(false); }
  };

  const initPctMap = (rows) => {
    const map={};
    rows.forEach(r=>(r.breakdowns||[]).forEach(b=>{ map[b.id]=fmtN(b.percentage_considered??100); }));
    setPctMap(map);
  };

  const extractIncentiveRows = (d) => {
    if (Array.isArray(d?.data)) return d.data;
    if (d?.data?.id) return [d.data];
    if (Array.isArray(d?.results)) return d.results;
    if (Array.isArray(d)) return d;
    if (d?.id) return [d];
    return [];
  };

  const applySelectedUserFilter = (rows) => {
    if (!selUser) return rows;
    return rows.filter(r => String(r.user || r.user_detail?.id || '') === String(selUser));
  };

  const handleCalc = async () => {
    setCalculating(true); setCalcDone(false); setCalcData([]); setPctMap({});
    try {
      const params=ymParams(ym);
      if (selUser) params.user_id=selUser;
      const res  = await api.calculateIncentive(params);
      const d    = res?.data;
      const rows = applySelectedUserFilter(extractIncentiveRows(d));
      if (!rows.length) { toast.info('No eligible cases found.'); }
      else {
        setCalcData(rows); setCalcDone(true);
        setIsLocked(rows.some(r=>r.is_locked));
        initPctMap(rows);
        toast.success(`Calculated for ${rows.length} user${rows.length!==1?'s':''}`);
      }
    } catch(e) { toast.error(e?.message||'Calculation failed'); }
    finally { setCalculating(false); }
  };

  const handleLock = async () => {
    if (!window.confirm(`Lock incentives for ${ymLabel(ym)}? This cannot be undone.`)) return;
    setLocking(true);
    try {
      const payload = {
        ...ymParams(ym),
        percentages: getPctPayload(calcData, pctMap),
      };
      if (selUser) payload.user_id = selUser;
      const res = await api.lockIncentive(payload);
      const d = res?.data;
      const rows = Array.isArray(d?.data) ? d.data : d?.data ? [d.data] : [];
      if (rows.length) {
        setCalcData(rows);
        initPctMap(rows);
      }
      setIsLocked(true);
      toast.success(`Locked - ${ymLabel(ym)}`);
      loadExisting();
    }
    catch(e) { toast.error(e?.message||'Lock failed'); }
    finally { setLocking(false); }
  };
  const handlePctChange = (id, val) => {
    setPctMap(prev=>({...prev,[id]:Math.max(0,Math.min(100,parseFloat(val)||0))}));
  };

  const handleExport = (format = 'csv') => {
    if (!calcData.length) { toast.error('Calculate first'); return; }
    setExporting(true);
    try {
      if (format === 'excel') exportExcelReport(calcData,pctMap,ym,ymLabel(ym));
      else exportCsv(calcData,pctMap,ym,ymLabel(ym));
    }
    catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const TABS = [
    { id:'sheet1', label:'Sheet1 — Case-wise', icon:<FiActivity size={13}/>,  color:C.teal   },
    { id:'sheet2', label:'Sheet2 — Incentive',  icon:<FiUsers   size={13}/>,  color:C.purple },
  ];

  return (
    <>
      <style>{`
        @keyframes incShimmer { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
        @keyframes incSpin    { from{transform:rotate(0)} to{transform:rotate(360deg)} }
        @keyframes incFadeUp  { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        .inc-wrap { animation: incFadeUp 0.2s ease both; }
        .inc-tr:hover td { background:#F0F7FF !important; }
        .inc-pct::-webkit-inner-spin-button { opacity:1; }
      `}</style>

      <Layout pageTitle="Incentive Management">
        <div className="inc-wrap" style={{display:'flex',flexDirection:'column',gap:14,
          fontFamily:"'Plus Jakarta Sans',sans-serif",paddingBottom:32}}>

          {/* ═══ HERO ═══ */}
          <div style={{borderRadius:14,background:'linear-gradient(120deg,#0D3B5E,#1A5276)',
            boxShadow:'0 4px 20px rgba(0,0,0,0.18)',position:'relative',overflow:'hidden'}}>
            <div style={{position:'absolute',top:0,left:0,right:0,height:3,
              background:'linear-gradient(90deg,#F5A623,#FBBF4A,transparent)'}}/>
            <div style={{padding:'20px 28px',display:'flex',justifyContent:'space-between',
              alignItems:'center',flexWrap:'wrap',gap:14}}>
              <div>
                <div style={{fontSize:11,fontWeight:600,color:'rgba(255,255,255,0.48)',
                  marginBottom:3,textTransform:'uppercase',letterSpacing:'0.06em'}}>Finance</div>
                <h2 style={{margin:0,color:'#fff',fontSize:20,fontWeight:800,letterSpacing:'-0.03em'}}>
                  Incentive Management
                </h2>
                <div style={{fontSize:12.5,color:'rgba(255,255,255,0.55)',marginTop:4}}>
                  Sheet1: Case-wise CF Revenue &nbsp;·&nbsp; Sheet2: D=(A−B)×C → Eligible Incentive
                </div>
              </div>
              {/* Month nav */}
              <div style={{display:'flex',alignItems:'center',gap:8,
                background:'rgba(255,255,255,0.10)',borderRadius:10,
                border:'1px solid rgba(255,255,255,0.18)',padding:'6px 10px'}}>
                <button onClick={()=>setYm(prevYM(ym))}
                  style={{width:28,height:28,borderRadius:7,background:'rgba(255,255,255,0.12)',
                    border:'1px solid rgba(255,255,255,0.20)',color:'rgba(255,255,255,0.75)',
                    cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
                  <FiChevronLeft size={13}/>
                </button>
                <span style={{fontSize:13.5,fontWeight:700,color:'#fff',minWidth:130,textAlign:'center'}}>
                  {ymLabel(ym)}
                </span>
                <button onClick={()=>{ if(ym<curYM()) setYm(nextYM(ym)); }}
                  disabled={ym>=curYM()}
                  style={{width:28,height:28,borderRadius:7,background:'rgba(255,255,255,0.12)',
                    border:'1px solid rgba(255,255,255,0.20)',
                    color:ym>=curYM()?'rgba(255,255,255,0.25)':'rgba(255,255,255,0.75)',
                    cursor:ym>=curYM()?'not-allowed':'pointer',
                    display:'flex',alignItems:'center',justifyContent:'center'}}>
                  <FiChevronRight size={13}/>
                </button>
                {isLocked&&(
                  <span style={{fontSize:10.5,fontWeight:700,color:'#4ADE80',
                    display:'flex',alignItems:'center',gap:3}}>
                    <FiLock size={10}/> LOCKED
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* ═══ FORMULA INFO ═══ */}
          <div style={{background:C.navyL,borderRadius:11,border:`1px solid ${C.navy}20`,
            padding:'10px 18px',display:'flex',alignItems:'flex-start',gap:10}}>
            <FiInfo size={14} style={{color:C.navy,flexShrink:0,marginTop:2}}/>
            <div style={{fontSize:12.5,color:C.navy,lineHeight:1.9}}>
              <strong>Sheet1:</strong>&nbsp;
              <code style={{background:C.white,borderRadius:5,padding:'1px 7px',fontSize:12}}>
                Incomes = Disbursed × Institution Payout % × Channel Payout %
              </code>
              &nbsp;&nbsp;
              <code style={{background:C.white,borderRadius:5,padding:'1px 7px',fontSize:12}}>
                CF Revenue = Incomes − BSA Payout
              </code>
              &nbsp;&nbsp;|&nbsp;&nbsp;
              <strong>Sheet2:</strong>&nbsp;
              <code style={{background:C.white,borderRadius:5,padding:'1px 7px',fontSize:12}}>
                D = (A − B) × C
              </code>
              &nbsp;&nbsp;
              <code style={{background:C.white,borderRadius:5,padding:'1px 7px',fontSize:12}}>
                Incentive = (ΣD − Cutoff) × Rate%
              </code>
              &nbsp;&nbsp;<em style={{fontSize:11.5}}>Column C editable (default 100%)</em>
            </div>
          </div>

          {/* ═══ ACTION BAR ═══ */}
          <div style={{background:C.white,borderRadius:12,border:`1px solid ${C.border}`,
            padding:'14px 18px',boxShadow:'0 2px 6px rgba(13,59,94,0.06)',
            display:'flex',alignItems:'center',flexWrap:'wrap',gap:12}}>
            <div>
              <label style={{fontSize:11,fontWeight:700,color:C.t4,textTransform:'uppercase',
                letterSpacing:'0.07em',display:'block',marginBottom:5}}>User</label>
              <select value={selUser} onChange={e=>setSelUser(e.target.value)}
                style={{height:36,border:`1px solid ${C.border}`,borderRadius:8,
                  padding:'0 10px',fontSize:13,color:C.t1,background:C.white,
                  outline:'none',fontFamily:'inherit',minWidth:180}}>
                <option value=''>All Users</option>
                {users.map(u=>{
                  const n=[u.first_name,u.last_name].filter(Boolean).join(' ')||u.email;
                  return <option key={u.id} value={u.id}>{n}</option>;
                })}
              </select>
            </div>
            <div style={{flex:1}}/>
            <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
              {!isLocked&&(
                <button onClick={handleCalc} disabled={calculating||locking}
                  style={{display:'flex',alignItems:'center',gap:6,height:38,
                    padding:'0 20px',borderRadius:9,border:'none',
                    background:calculating?C.navyM:C.navy,color:'#fff',
                    fontWeight:700,fontSize:13,
                    cursor:calculating?'not-allowed':'pointer',fontFamily:'inherit'}}>
                  {calculating?<><Spin/> Calculating…</>:<><FiPlay size={13}/> Calculate</>}
                </button>
              )}
              {calcDone&&!isLocked&&(
                <button onClick={handleLock} disabled={locking}
                  style={{display:'flex',alignItems:'center',gap:6,height:38,
                    padding:'0 18px',borderRadius:9,border:'none',
                    background:locking?C.green:C.greenB,color:'#fff',
                    fontWeight:700,fontSize:13,
                    cursor:locking?'not-allowed':'pointer',fontFamily:'inherit'}}>
                  {locking?<><Spin/> Locking…</>:<><FiLock size={13}/> Lock Month</>}
                </button>
              )}
              {(calcDone||isLocked)&&(
                <>
                  <button onClick={()=>handleExport('csv')} disabled={exporting}
                    style={{display:'flex',alignItems:'center',gap:6,height:38,
                      padding:'0 18px',borderRadius:9,border:'none',
                      background:exporting?'#94A3B8':C.tealB,color:'#fff',
                      fontWeight:700,fontSize:13,
                      cursor:exporting?'not-allowed':'pointer',fontFamily:'inherit'}}>
                    {exporting?<><Spin/> Exporting...</>:<><FiDownload size={13}/> Export CSV</>}
                  </button>
                  <button onClick={()=>handleExport('excel')} disabled={exporting}
                    style={{display:'flex',alignItems:'center',gap:6,height:38,
                      padding:'0 18px',borderRadius:9,border:`1px solid ${C.tealB}`,
                      background:exporting?C.borderL:C.white,color:exporting?C.t4:C.teal,
                      fontWeight:700,fontSize:13,
                      cursor:exporting?'not-allowed':'pointer',fontFamily:'inherit'}}>
                    <FiDownload size={13}/> Export Excel
                  </button>
                </>
              )}
              <button onClick={loadExisting} title="Refresh"
                style={{width:38,height:38,borderRadius:9,border:`1px solid ${C.border}`,
                  background:C.white,color:C.t3,cursor:'pointer',
                  display:'flex',alignItems:'center',justifyContent:'center'}}>
                <FiRefreshCw size={13} style={{animation:loading?'incSpin 1s linear infinite':'none'}}/>
              </button>
            </div>
          </div>

          {/* ═══ LOCKED BANNER ═══ */}
          {isLocked&&(
            <div style={{background:C.greenL,border:`1px solid #86EFAC`,borderRadius:10,
              padding:'11px 16px',display:'flex',alignItems:'center',gap:10}}>
              <FiLock size={14} style={{color:C.greenB,flexShrink:0}}/>
              <span style={{fontSize:13,fontWeight:700,color:C.green}}>
                Locked — {ymLabel(ym)} &nbsp;·&nbsp;
                <span style={{fontWeight:400,color:'#15803D'}}>Records are final and read-only.</span>
              </span>
            </div>
          )}

          {/* ═══ EMPTY STATE ═══ */}
          {!loading&&!calculating&&!calcDone&&(
            <div style={{background:C.white,borderRadius:12,border:`1px solid ${C.border}`,
              padding:'60px 24px',textAlign:'center'}}>
              <div style={{width:56,height:56,borderRadius:'50%',background:C.navyL,
                display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 14px'}}>
                <span style={{fontSize:22,fontWeight:700,color:C.navy,opacity:0.5}}>₹</span>
              </div>
              <div style={{fontSize:14,fontWeight:700,color:C.t1,marginBottom:6}}>
                No data for {ymLabel(ym)}
              </div>
              <div style={{fontSize:13,color:C.t3,marginBottom:18}}>
                Select user and month, then click <strong>Calculate</strong>
              </div>
              <button onClick={handleCalc} disabled={calculating}
                style={{display:'inline-flex',alignItems:'center',gap:7,
                  padding:'10px 24px',borderRadius:9,border:'none',
                  background:C.navy,color:'#fff',fontWeight:700,fontSize:13,
                  cursor:'pointer',fontFamily:'inherit'}}>
                <FiPlay size={13}/> Calculate {ymLabel(ym)}
              </button>
            </div>
          )}

          {/* ═══ TABS ═══ */}
          {(calcDone||loading||calculating)&&(
            <>
              {/* Tab bar */}
              <div style={{display:'flex',gap:4,background:C.white,borderRadius:12,
                border:`1px solid ${C.border}`,padding:6,
                boxShadow:'0 2px 6px rgba(13,59,94,0.06)'}}>
                {TABS.map(tab=>{
                  const isActive = activeTab===tab.id;
                  return (
                    <button key={tab.id} onClick={()=>setActiveTab(tab.id)}
                      style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',
                        gap:7,padding:'9px 16px',borderRadius:9,border:'none',
                        background:isActive?tab.color:'transparent',
                        color:isActive?C.white:C.t3,
                        fontWeight:isActive?700:500,fontSize:13,
                        cursor:'pointer',fontFamily:'inherit',
                        transition:'all 0.15s',
                        boxShadow:isActive?`0 2px 8px ${tab.color}40`:'none'}}>
                      <span style={{color:isActive?C.white:tab.color}}>{tab.icon}</span>
                      {tab.label}
                      {calcDone&&(
                        <span style={{
                          background:isActive?'rgba(255,255,255,0.25)':'rgba(0,0,0,0.06)',
                          color:isActive?C.white:C.t3,
                          padding:'1px 7px',borderRadius:20,fontSize:10.5,fontWeight:700,
                        }}>
                          {tab.id==='sheet1'
                            ? calcData.reduce((s,r)=>s+(r.breakdowns||[]).length,0)
                            : calcData.length
                          }
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Tab content */}
              {activeTab==='sheet1'&&(
                <Sheet1Tab
                  calcData={calcData}
                  loading={loading}
                  calculating={calculating}
                />
              )}
              {activeTab==='sheet2'&&(
                <Sheet2Tab
                  calcData={calcData}
                  loading={loading}
                  calculating={calculating}
                  isLocked={isLocked}
                  pctMap={pctMap}
                  onPctChange={handlePctChange}
                />
              )}

              {/* Lock warning */}
              {calcDone&&!isLocked&&activeTab==='sheet2'&&(
                <div style={{background:C.redL,border:`1px solid ${C.redBord}`,borderRadius:10,
                  padding:'11px 16px',display:'flex',alignItems:'center',gap:10}}>
                  <FiAlertCircle size={13} style={{color:C.redB,flexShrink:0}}/>
                  <span style={{fontSize:12.5,color:'#7F1D1D'}}>
                    <strong>Before locking:</strong> Verify all Column C (% Considered) values.
                    Once locked for <strong>{ymLabel(ym)}</strong>, this cannot be changed.
                  </span>
                </div>
              )}
            </>
          )}

        </div>
      </Layout>
    </>
  );
}


